import {
  type MessagePathPart,
  MessagePathPartType,
  type MessagePathSlice,
} from "@/shared/domain/topics/MessagePathRecord";
import { MessagePayload } from "@/shared/webworker";

/**
 * Generates a unique key for a message path entry, disambiguating
 * different array slices of the same field (which share the same id).
 */
export function messagePathKey(mp: {
  id: string;
  parts: MessagePathPart[];
}): string {
  const sliceSuffix = mp.parts
    .filter((p): p is MessagePathSlice => p.type === MessagePathPartType.Slice)
    .map(
      (p) =>
        `[${p.start}:${p.end !== null && p.end !== undefined && isFinite(p.end) ? p.end : ""}]`,
    )
    .join("");
  return sliceSuffix ? `${mp.id}::${sliceSuffix}` : mp.id;
}

export enum DataServerCommand {
  GetAllData = "get-all-data",
  Init = "init",
}

export enum DataServerCommandResponse {
  GetAllDataResponse = "get-all-data-response",
  InitResponse = "init-response",
}

export type TopicIdToMessagePathValues = Record<
  string,
  { logtimes: bigint[]; values: Record<string, number[]> }
>;

export type TopicIdToMessagePaths = Record<
  string,
  Record<string, { id: string; parts: MessagePathPart[] }>
>;

export interface DataServerCommandPayloadMap {
  [DataServerCommand.GetAllData]: TopicIdToMessagePaths;
  [DataServerCommand.Init]: undefined;
}

export interface DataServerCommandResponsePayloadMap {
  [DataServerCommandResponse.GetAllDataResponse]: TopicIdToMessagePathValues;
  [DataServerCommandResponse.InitResponse]: undefined;
}

export function isDataServerInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.Init]
  >
> {
  return event.data.type === DataServerCommand.Init;
}

export function isDataServerGetAllDataCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.GetAllData]
  >
> {
  return event.data.type === DataServerCommand.GetAllData;
}
